/**
 * Excel export utilities for dashboard reports.
 * Uses xlsx-js-style (SheetJS with styling support) + file-saver to produce .xlsx workbooks.
 */

import * as XLSX from 'xlsx-js-style'
import { saveAs } from 'file-saver'
import type { DashboardData, TrendData } from '../hooks/useDashboard'
import type { TeamDashboardData } from '../hooks/useTeamDashboard'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTrend(trend: TrendData | null | undefined): string {
  if (!trend) return '—'
  const sign = trend.direction === 'up' ? '▲' : trend.direction === 'down' ? '▼' : '—'
  const val =
    trend.type === 'percentage'
      ? `${trend.value.toFixed(1)}%`
      : trend.value.toFixed(0)
  return `${sign} ${val} ${trend.comparison_period}`
}

function titleCase(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

function saveWorkbook(wb: XLSX.WorkBook, filename: string): void {
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename)
}

function filenameDate(): string {
  return new Date().toISOString().slice(0, 10) // YYYY-MM-DD
}

/**
 * Apply bold formatting to the header row (first row) of a worksheet
 */
function boldHeaders(ws: XLSX.WorkSheet): void {
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')
  
  // Apply bold to all cells in the first row
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col })
    if (!ws[cellAddress]) continue
    
    // Set font to bold using xlsx-js-style format
    ws[cellAddress].s = {
      font: { 
        bold: true,
        sz: 11
      },
      alignment: {
        vertical: 'center',
        horizontal: 'left'
      }
    }
  }
}

// ─── User Dashboard Export ────────────────────────────────────────────────────

export async function exportUserDashboard(data: DashboardData, dateRange: string): Promise<void> {
  const wb = XLSX.utils.book_new()

  // Sheet 1 — Summary stats
  const summaryRows = [
    {
      Metric: 'Resumes Uploaded',
      Count: data.stats.resumes_uploaded.count,
      Trend: formatTrend(data.stats.resumes_uploaded.trend),
    },
    {
      Metric: 'Jobs Assigned',
      Count: data.stats.jobs_created.count,
      Trend: formatTrend(data.stats.jobs_created.trend),
    },
    {
      Metric: 'Matches Applied',
      Count: data.stats.matches_applied.count,
      Trend: formatTrend(data.stats.matches_applied.trend),
    },
  ]
  const summarySheet = XLSX.utils.json_to_sheet(summaryRows)
  boldHeaders(summarySheet)
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary')

  // Sheet 2 — Job Submissions
  const jobRows = data.job_submissions.map((j) => ({
    'Job ID': j.job_id ?? '',
    'Job Title': j.job_title,
    Client: j.client_name,
    'Submitted Count': j.submitted_count,
    'Pending Count': j.pending_count,
  }))
  const jobSheet = jobRows.length ? XLSX.utils.json_to_sheet(jobRows) : XLSX.utils.aoa_to_sheet([['No job submissions in this period']])
  if (jobRows.length) boldHeaders(jobSheet)
  XLSX.utils.book_append_sheet(wb, jobSheet, 'Job Submissions')

  // Sheet 3 — Match Status Breakdown
  const statusRows = data.status_breakdown.map((s) => ({
    Status: titleCase(s.status),
    Count: s.count,
    Percentage: `${s.percentage}%`,
  }))
  const statusSheet = statusRows.length ? XLSX.utils.json_to_sheet(statusRows) : XLSX.utils.aoa_to_sheet([['No status data in this period']])
  if (statusRows.length) boldHeaders(statusSheet)
  XLSX.utils.book_append_sheet(wb, statusSheet, 'Match Status')

  // Fetch all sources for Excel export (not limited to top 10)
  try {
    const { analyticsApi } = await import('../services/api')
    
    // Detect user's timezone
    let userTimezone = 'UTC'
    try {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (e) {
      console.warn('Failed to detect timezone, using UTC:', e)
    }
    
    const queryParams = new URLSearchParams({
      date_range: dateRange,
      include_all_sources: 'true',
      timezone: userTimezone
    })
    const allSourcesResponse = await analyticsApi.getDashboardStats(queryParams.toString())
    const allSourcesData = allSourcesResponse.data

    // Sheet 4 — Resume Sources
    const resumeSourceRows = allSourcesData.resume_sources.map((s: any) => ({
      'Source Name': s.source_name,
      Count: s.count,
    }))
    const resumeSourceSheet = resumeSourceRows.length ? XLSX.utils.json_to_sheet(resumeSourceRows) : XLSX.utils.aoa_to_sheet([['No resume sources in this period']])
    if (resumeSourceRows.length) boldHeaders(resumeSourceSheet)
    XLSX.utils.book_append_sheet(wb, resumeSourceSheet, 'Resume Sources')

    // Sheet 5 — Job Sources
    const jobSourceRows = allSourcesData.job_sources.map((s: any) => ({
      'Source Name': s.source_name,
      Count: s.count,
    }))
    const jobSourceSheet = jobSourceRows.length ? XLSX.utils.json_to_sheet(jobSourceRows) : XLSX.utils.aoa_to_sheet([['No job sources in this period']])
    if (jobSourceRows.length) boldHeaders(jobSourceSheet)
    XLSX.utils.book_append_sheet(wb, jobSourceSheet, 'Job Sources')
  } catch (error) {
    console.error('Failed to fetch all sources for export:', error)
    // Add empty sheets if fetch fails
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Failed to load source data']]), 'Resume Sources')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Failed to load source data']]), 'Job Sources')
  }

  saveWorkbook(wb, `dashboard-report-${dateRange}-${filenameDate()}.xlsx`)
}

// ─── Job Postings Export ─────────────────────────────────────────────────────

export interface JobExportFilters {
  status?: string
  client?: string
  search?: string
  source_id?: string
  sort_by?: string
  sort_order?: string
}

export async function exportJobPostings(format: 'xlsx' | 'csv', filters: JobExportFilters = {}): Promise<void> {
  const { jobsApi } = await import('../services/api')

  const response = await jobsApi.list({
    ...filters,
    page: 1,
    page_size: 1000,
  })
  const jobs: any[] = response.data?.jobs ?? []

  const rows = jobs.map((j) => ({
    'Job ID': j.job_id ?? '',
    'Title': j.title ?? '',
    'Client': j.client ?? '',
    'Department': j.department ?? '',
    'Location': j.location ?? '',
    'Employment Type': titleCase(j.employment_type ?? ''),
    'Remote Option': titleCase(j.remote_option ?? ''),
    'Status': j.status === 'active' ? 'Open' : j.status === 'closed' ? 'Filled' : titleCase(j.status ?? ''),
    'Source': j.source_name ?? '',
    'Salary Min': j.salary_min ?? '',
    'Salary Max': j.salary_max ?? '',
    'Currency': j.salary_currency ?? '',
    'Salary Period': titleCase(j.salary_period ?? ''),
    'Min Experience (yrs)': j.min_years_experience ?? '',
    'Max Experience (yrs)': j.max_years_experience ?? '',
    'Created By': j.created_by_name ?? '',
    'Created Date': j.created_at ? new Date(j.created_at).toLocaleDateString() : '',
    'Assigned Recruiters': (j.assigned_recruiters ?? []).map((r: any) => r.full_name ?? r.email).join(', '),
  }))

  const date = filenameDate()
  const clientLabel = filters.client ? `-${filters.client}` : ''
  const statusLabel = filters.status ? `-${filters.status}` : ''
  const filename = `job-postings-report${clientLabel}${statusLabel}-${date}`

  if (format === 'csv') {
    if (rows.length === 0) return
    const headers = Object.keys(rows[0])
    const csvRows = [
      headers.join(','),
      ...rows.map((row) =>
        headers.map((h) => {
          const val = String(row[h as keyof typeof row] ?? '')
          return val.includes(',') || val.includes('"') || val.includes('\n')
            ? `"${val.replace(/"/g, '""')}"`
            : val
        }).join(',')
      ),
    ]
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, `${filename}.csv`)
    return
  }

  const wb = XLSX.utils.book_new()
  const ws = rows.length
    ? XLSX.utils.json_to_sheet(rows)
    : XLSX.utils.aoa_to_sheet([['No job postings found for the selected filters']])
  if (rows.length) boldHeaders(ws)
  XLSX.utils.book_append_sheet(wb, ws, 'Job Postings')
  saveWorkbook(wb, `${filename}.xlsx`)
}

// ─── Team Dashboard Export ────────────────────────────────────────────────────

export async function exportTeamDashboard(data: TeamDashboardData, dateRange: string): Promise<void> {
  const wb = XLSX.utils.book_new()

  // Sheet 1 — Team Summary
  const summaryRows = [
    {
      Metric: 'Team Resumes Uploaded',
      Count: data.stats.resumes_uploaded.count,
      Trend: formatTrend(data.stats.resumes_uploaded.trend),
    },
    {
      Metric: 'Team Jobs Assigned',
      Count: data.stats.jobs_created.count,
      Trend: formatTrend(data.stats.jobs_created.trend),
    },
    {
      Metric: 'Team Matches Applied',
      Count: data.stats.matches_applied.count,
      Trend: formatTrend(data.stats.matches_applied.trend),
    },
  ]
  const summarySheet = XLSX.utils.json_to_sheet(summaryRows)
  boldHeaders(summarySheet)
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Team Summary')

  // Sheet 2 — Member Activity
  const memberRows = data.member_stats.map((m) => ({
    Name: m.full_name,
    Email: m.email,
    Role: titleCase(m.role),
    'Resumes Uploaded': m.resumes_uploaded,
    'Jobs Assigned': m.jobs_assigned,
    'Matches Applied': m.matches_applied,
  }))
  const memberSheet = memberRows.length ? XLSX.utils.json_to_sheet(memberRows) : XLSX.utils.aoa_to_sheet([['No member data']])
  if (memberRows.length) boldHeaders(memberSheet)
  XLSX.utils.book_append_sheet(wb, memberSheet, 'Member Activity')

  // Sheet 3 — Job Submissions
  const jobRows = data.job_submissions.map((j) => ({
    'Job ID': j.job_id ?? '',
    'Job Title': j.job_title,
    Client: j.client_name,
    'Submitted Count': j.submitted_count,
    'Pending Count': j.pending_count,
    'Created By': j.created_by_name ?? '',
    'Created Date': j.created_at ? new Date(j.created_at).toLocaleDateString() : '',
    'Assigned Recruiters': (j.assigned_recruiters ?? []).join(', '),
  }))
  const jobSheet = jobRows.length ? XLSX.utils.json_to_sheet(jobRows) : XLSX.utils.aoa_to_sheet([['No job submissions in this period']])
  if (jobRows.length) boldHeaders(jobSheet)
  XLSX.utils.book_append_sheet(wb, jobSheet, 'Job Submissions')

  // Sheet 4 — Match Status Breakdown
  const statusRows = data.status_breakdown.map((s) => ({
    Status: titleCase(s.status),
    Count: s.count,
    Percentage: `${s.percentage}%`,
  }))
  const statusSheet = statusRows.length ? XLSX.utils.json_to_sheet(statusRows) : XLSX.utils.aoa_to_sheet([['No status data in this period']])
  if (statusRows.length) boldHeaders(statusSheet)
  XLSX.utils.book_append_sheet(wb, statusSheet, 'Match Status')

  // Sheet 5 — Teammate Assignments & Submissions (flat rows)
  const assignRows: Record<string, string | number>[] = []
  for (const member of data.member_job_breakdown) {
    if (member.jobs.length === 0) {
      assignRows.push({
        Member: member.full_name,
        Role: titleCase(member.role),
        'Job ID': '',
        'Job Title': 'No jobs assigned in this period',
        Client: '',
        Submissions: 0,
        'Created By': '',
        'Created Date': '',
        'Assigned Recruiters': '',
      })
    } else {
      for (const job of member.jobs) {
        assignRows.push({
          Member: member.full_name,
          Role: titleCase(member.role),
          'Job ID': job.job_id ?? '',
          'Job Title': job.job_title,
          Client: job.client_name,
          Submissions: job.submission_count,
          'Created By': job.created_by_name ?? '',
          'Created Date': job.created_at ? new Date(job.created_at).toLocaleDateString() : '',
          'Assigned Recruiters': (job.assigned_recruiters ?? []).join(', '),
        })
      }
    }
  }
  const assignSheet = assignRows.length ? XLSX.utils.json_to_sheet(assignRows) : XLSX.utils.aoa_to_sheet([['No teammate data']])
  if (assignRows.length) boldHeaders(assignSheet)
  XLSX.utils.book_append_sheet(wb, assignSheet, 'Teammate Assignments')

  // Fetch all sources for Excel export (not limited to top 10)
  try {
    const { analyticsApi } = await import('../services/api')
    
    // Detect user's timezone
    let userTimezone = 'UTC'
    try {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (e) {
      console.warn('Failed to detect timezone, using UTC:', e)
    }
    
    const queryParams = new URLSearchParams({
      date_range: dateRange,
      include_all_sources: 'true',
      timezone: userTimezone
    })
    const allSourcesResponse = await analyticsApi.getTeamDashboardStats(queryParams.toString())
    const allSourcesData = allSourcesResponse.data

    // Sheet 6 — Resume Sources
    const resumeSourceRows = allSourcesData.resume_sources.map((s: any) => ({
      'Source Name': s.source_name,
      Count: s.count,
    }))
    const resumeSourceSheet = resumeSourceRows.length ? XLSX.utils.json_to_sheet(resumeSourceRows) : XLSX.utils.aoa_to_sheet([['No resume sources in this period']])
    if (resumeSourceRows.length) boldHeaders(resumeSourceSheet)
    XLSX.utils.book_append_sheet(wb, resumeSourceSheet, 'Resume Sources')

    // Sheet 7 — Job Sources
    const jobSourceRows = allSourcesData.job_sources.map((s: any) => ({
      'Source Name': s.source_name,
      Count: s.count,
    }))
    const jobSourceSheet = jobSourceRows.length ? XLSX.utils.json_to_sheet(jobSourceRows) : XLSX.utils.aoa_to_sheet([['No job sources in this period']])
    if (jobSourceRows.length) boldHeaders(jobSourceSheet)
    XLSX.utils.book_append_sheet(wb, jobSourceSheet, 'Job Sources')
  } catch (error) {
    console.error('Failed to fetch all sources for export:', error)
    // Add empty sheets if fetch fails
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Failed to load source data']]), 'Resume Sources')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Failed to load source data']]), 'Job Sources')
  }

  saveWorkbook(wb, `team-dashboard-report-${dateRange}-${filenameDate()}.xlsx`)
}
